import { Link } from "react-router-dom";
import { CalendarCheck, CircleCheck } from "lucide-react";
import ImageSlot from "@/components/ImageSlot";
import { FadeUp, MaskedLine, Stagger, StaggerItem } from "@/components/Reveal";
import { SERVICES } from "@/data/site";

const SERVICE_IMAGES = {
    "igiene-prevenzione": "/images/svc-igiene.webp",
    conservativa: "/images/svc-conservativa.webp",
    endodonzia: "/images/svc-endodonzia.webp",
    "protesi-laboratorio": "/images/svc-protesi.webp",
    implantologia: "/images/svc-implantologia.webp",
    "chirurgia-orale": "/images/svc-chirurgia.webp",
    patologia: "/images/svc-patologia.webp",
    ortodonzia: "/images/svc-ortodonzia.webp",
    estetica: "/images/svc-estetica.webp",
    parodontologia: "/images/svc-parodontologia.webp",
    gnatologia: "/images/svc-gnatologia.webp",
    pedodonzia: "/images/svc-pedodonzia.webp",
};

export default function Servizi() {
    return (
        <>
            <section
                data-testid="servizi-hero"
                className="bg-white pt-32 pb-16 sm:pt-40 md:pb-20"
            >
                <div className="mx-auto max-w-7xl px-4 sm:px-8 md:px-12">
                    <FadeUp y={14}>
                        <p className="flex items-center gap-3 text-xs font-medium uppercase tracking-[0.3em] text-petrol">
                            <span className="h-px w-10 bg-petrol" />
                            Le Branche dello Studio
                        </p>
                    </FadeUp>
                    <h1 className="mt-7 max-w-3xl font-serif text-4xl font-normal leading-[1.08] tracking-tight text-ink sm:text-5xl md:text-6xl">
                        <MaskedLine delay={0.15}>Dodici specialità,</MaskedLine>
                        <MaskedLine delay={0.3}>
                            <span className="italic text-petrol">un unico studio.</span>
                        </MaskedLine>
                    </h1>
                    <FadeUp delay={0.5}>
                        <p className="mt-6 max-w-2xl text-base font-light leading-relaxed tracking-wide text-body sm:text-lg">
                            Dalla prevenzione alla riabilitazione completa: ogni
                            percorso di cura è seguito direttamente dal Dr.
                            Massironi e il suo staff.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.6}>
                        <nav
                            data-testid="servizi-quicknav"
                            aria-label="Vai alla branca"
                            className="mt-10 flex gap-3 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                        >
                            {SERVICES.map((s) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    data-testid={`quicknav-${s.id}`}
                                    onClick={() =>
                                        window.__lenis
                                            ? window.__lenis.scrollTo(`#${s.id}`, { offset: -110 })
                                            : document.getElementById(s.id)?.scrollIntoView()
                                    }
                                    className="shrink-0 rounded-full border border-line bg-white px-5 py-2.5 text-xs font-normal tracking-wide text-body transition-all duration-300 hover:border-petrol hover:text-petrol"
                                >
                                    {s.name}
                                </button>
                            ))}
                        </nav>
                    </FadeUp>
                </div>
            </section>

            <section
                data-testid="servizi-lista"
                className="overflow-hidden bg-ice py-16 sm:py-20 md:py-24"
            >
                <div className="mx-auto max-w-7xl space-y-24 px-4 sm:px-8 md:space-y-32 md:px-12">
                    {SERVICES.map((s, i) => {
                        const img = SERVICE_IMAGES[s.id];
                        const flip = i % 2 === 1;
                        return (
                            <article
                                key={s.id}
                                id={s.id}
                                data-testid={`servizio-${s.id}`}
                                className="grid scroll-mt-28 grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14"
                            >
                                <div
                                    className={`lg:col-span-7 ${
                                        flip
                                            ? "lg:order-2 lg:mr-[calc(50%-50vw)]"
                                            : "lg:order-1 lg:ml-[calc(50%-50vw)]"
                                    }`}
                                >
                                    <FadeUp>
                                        <ImageSlot
                                            src={img}
                                            alt={s.name}
                                            ratio="aspect-[4/3]"
                                            className={
                                                flip
                                                    ? "rounded-r-none"
                                                    : "rounded-l-none"
                                            }
                                            testid={`servizio-image-${s.id}`}
                                        />
                                    </FadeUp>
                                </div>
                                <div
                                    className={`lg:col-span-5 ${
                                        flip ? "lg:order-1" : "lg:order-2"
                                    }`}
                                >
                                    <FadeUp y={16}>
                                        <div className="flex items-center gap-4">
                                            <span className="font-serif text-4xl font-light text-petrol/40">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-petrol-light text-petrol">
                                                <s.icon className="h-5 w-5" />
                                            </span>
                                            {s.highlight && (
                                                <span
                                                    data-testid="servizio-badge-lab"
                                                    className="rounded-full bg-petrol px-4 py-1.5 text-[0.62rem] font-medium uppercase tracking-[0.18em] text-white"
                                                >
                                                    Il nostro fiore all'occhiello
                                                </span>
                                            )}
                                        </div>
                                    </FadeUp>
                                    <h2 className="mt-5 font-serif text-2xl font-normal leading-tight text-ink sm:text-3xl">
                                        <MaskedLine delay={0.1}>{s.name}</MaskedLine>
                                    </h2>
                                    <FadeUp delay={0.2}>
                                        <p className="mt-4 max-w-xl text-base font-light leading-relaxed tracking-wide text-body">
                                            {s.long}
                                        </p>
                                    </FadeUp>
                                    <Stagger className="mt-7 grid gap-3">
                                        {s.points.map((p) => (
                                            <StaggerItem key={p}>
                                                <div className="flex items-start gap-2.5">
                                                    <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-petrol" />
                                                    <p className="text-sm font-light tracking-wide text-body">
                                                        {p}
                                                    </p>
                                                </div>
                                            </StaggerItem>
                                        ))}
                                    </Stagger>
                                    <FadeUp delay={0.25}>
                                        <Link
                                            to={`/contatti?servizio=${s.id}`}
                                            data-testid={`servizio-cta-${s.id}`}
                                            className="mt-8 inline-flex items-center gap-2 rounded-full border border-petrol px-6 py-3 text-sm font-normal text-petrol transition-all duration-300 hover:bg-petrol hover:text-white active:scale-[0.98]"
                                        >
                                            <CalendarCheck className="h-4 w-4" />
                                            Richiedi una valutazione
                                        </Link>
                                    </FadeUp>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>

            <section
                data-testid="servizi-cta-finale"
                className="bg-white py-20 text-center sm:py-24"
            >
                <div className="mx-auto max-w-2xl px-4">
                    <h2 className="font-serif text-3xl font-normal leading-tight text-ink sm:text-4xl">
                        <MaskedLine>Non sai da dove cominciare?</MaskedLine>
                    </h2>
                    <FadeUp delay={0.2}>
                        <p className="mt-4 text-base font-light leading-relaxed text-body">
                            La prima visita serve proprio a questo: capire insieme
                            di cosa ha bisogno il tuo sorriso.
                        </p>
                        <Link
                            to="/contatti"
                            data-testid="servizi-cta-prenota"
                            className="mt-8 inline-flex items-center gap-3 rounded-full bg-petrol px-8 py-4 text-sm font-normal text-white shadow-sm transition-all duration-300 hover:bg-petrol-dark hover:shadow-md active:scale-[0.98]"
                        >
                            <CalendarCheck className="h-4 w-4" />
                            Prenota la prima visita
                        </Link>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
